import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchOrdersAction } from "../../../redux/slices/orders/ordersSlices";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";
import NoDataFound from "../../NoDataFound/NoDataFound";

export default function Customers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortBy, setSortBy] = useState("totalSpent");
  const [sortOrder, setSortOrder] = useState("desc");
  const [customersData, setCustomersData] = useState([]);
  
  useEffect(() => {
    dispatch(fetchOrdersAction());
  }, [dispatch]);
  
  //get data from store
  const { error, loading, orders } = useSelector((state) => state?.orders);
  const allOrders = orders?.orders || [];
  
  // Process customers from orders
  useEffect(() => {
    const processCustomers = () => {
      if (!allOrders.length) return;
      
      const customerMap = new Map();
      
      allOrders.forEach((order) => {
        // Get user ID from order
        const userId = order.user;
        
        // Get customer data from shippingAddress (since it contains all the info)
        const shippingAddress = order.shippingAddress || {};
        
        if (userId) {
          if (!customerMap.has(userId)) {
            // Initialize customer with data from shipping address
            customerMap.set(userId, {
              id: userId,
              fullname: shippingAddress.nombre && shippingAddress.paterno 
                ? `${shippingAddress.nombre} ${shippingAddress.paterno}`
                : "N/A",
              email: "N/A", // Email not stored in orders, will try to get from profile if available
              phone: shippingAddress.telefono || "N/A",
              address: shippingAddress.direccion || "N/A",
              city: shippingAddress.delegacion || "N/A",
              state: shippingAddress.estado || "N/A",
              country: shippingAddress.colonia || "N/A",
              postalCode: shippingAddress.zipcode || "N/A",
              totalOrders: 0,
              totalSpent: 0,
              lastOrderDate: null,
              firstOrderDate: null,
            });
          }
          
          const customer = customerMap.get(userId);
          // Update customer stats
          customer.totalOrders += 1;
          customer.totalSpent += order.totalPrice || 0;
          
          // Update dates
          const orderDate = new Date(order.createdAt);
          if (!customer.firstOrderDate || orderDate < customer.firstOrderDate) {
            customer.firstOrderDate = orderDate;
          }
          if (!customer.lastOrderDate || orderDate > customer.lastOrderDate) {
            customer.lastOrderDate = orderDate;
          }
          
          // Update customer info with latest shipping address (in case they changed it)
          if (shippingAddress.nombre) {
            customer.fullname = `${shippingAddress.nombre} ${shippingAddress.paterno || ''}`;
            customer.phone = shippingAddress.telefono || customer.phone;
            customer.address = shippingAddress.direccion || customer.address;
            customer.city = shippingAddress.delegacion || customer.city;
            customer.state = shippingAddress.estado || customer.state;
            customer.country = shippingAddress.colonia || customer.country;
            customer.postalCode = shippingAddress.zipcode || customer.postalCode;
          }
          
          customerMap.set(userId, customer);
        }
      });
      
      // Convert map to array
      let customers = Array.from(customerMap.values());
      
      // Sort customers
      customers.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        
        if (sortBy === "firstOrderDate" || sortBy === "lastOrderDate") {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }
        
        if (sortOrder === "desc") {
          return bValue > aValue ? 1 : -1;
        } else {
          return aValue > bValue ? 1 : -1;
        }
      });
      
      setCustomersData(customers);
    };
    
    processCustomers();
  }, [allOrders, sortBy, sortOrder]);
  
  // Handle sort click
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingComponent />
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <ErrorMsg message={error?.message || t('customers.errorLoading')} />
      </div>
    );
  }
  
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">
            {t('customers.title')}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('customers.description', { count: customersData.length })}
          </p>
        </div>
      </div>
      
      <div className="mt-8 flex flex-col">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {customersData.length === 0 ? (
                <NoDataFound message={t('customers.noCustomers')} />
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("fullname")}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('customers.fullName')}</span>
                          {sortBy === "fullname" && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sortOrder === "desc" ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("totalOrders")}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('customers.totalOrders')}</span>
                          {sortBy === "totalOrders" && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sortOrder === "desc" ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("totalSpent")}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('customers.totalSpent')}</span>
                          {sortBy === "totalSpent" && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sortOrder === "desc" ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("lastOrderDate")}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('customers.lastOrder')}</span>
                          {sortBy === "lastOrderDate" && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sortOrder === "desc" ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t('customers.phone')}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t('customers.address')}
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      >
                        {t('customers.city')}
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
                      >
                        {t('customers.postalCode')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {customersData.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {customer.fullname}
                        </td>
                        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {customer.totalOrders}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className="font-medium text-gray-900">
                            ${customer.totalSpent.toFixed(2)}
                          </span>
                        </td>
                        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                          {formatDate(customer.lastOrderDate)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {customer.phone !== "N/A" ? customer.phone : "—"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {customer.address !== "N/A" ? customer.address : "—"}
                        </td>
                        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
                          {customer.city !== "N/A" ? customer.city : "—"}
                        </td>
                        <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                          {customer.postalCode !== "N/A" ? customer.postalCode : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}